import React from 'react';
import PropTypes from 'prop-types';

import GewichtChartPanel from './GewichtGaugeChart';
import SpekdikteChartPanel from './SpekdikteGaugeChart';
import GewichtstoeslagChartPanel from './GewichtstoeslagGaugeChart';
import SourceDataButton, {
  pickFields,
  FieldSource,
  FooterSourceToggle,
  useFooterSource,
} from './ChartSourceModal';
import './StatisticsCharts.css';
import {
  WIDTH,
  HEIGHT,
  CX,
  CY,
  OUTER_R,
  INNER_R,
  START_ANGLE,
  END_ANGLE,
  parseLocaleNumber,
  polarToXY,
  valueToAngleForRange,
  arcPath,
  needlePath,
  needleValueToAngle,
  formatPercentage,
} from './gaugeGeometry';

const MIN = 1.8;
const MAX = 6.4;
const NUM_SEGMENTS = 15;

// 14 labels assigned to segments 1–14; segment 15 has no label
const SEGMENT_LABELS = [1.8, 2.2, 2.5, 2.8, 3.1, 3.4, 3.75, 4.1, 4.4, 4.7, 5, 5.4, 5.8, 6.4];

const KWALITEITSTOESLAG_VALUES = [
  -0.31, -0.26, -0.16, -0.11, -0.07, -0.03, 0,
  0.02, 0.035, 0.045, 0.055, 0.065, 0.075, 0.085,
];

// The Kwaliteitstoeslag legend shows its values with 4 decimals.
const KWALITEITSTOESLAG_DECIMALS = 4;

const KWALITEITSTOESLAG_LABELS = [
  '-0,31', '-0,26', '-0,16', '-0,11', '-0,07', '-0,03', '0',
  '0,02', '0,035', '0,045', '0,055', '0,065', '0,075', '0,085',
];

const SEGMENT_COLORS = [
  '#8B0000', '#8B0000', '#8B0000', '#8B0000', '#8B0000',
  '#e06666', '#F5D4A2', '#F2DCBB', '#EDE3D3', '#DBEDD3',
  '#BDE5AA', '#A1CF8C', '#97D678', '#97D678', '#97D678',
];

const VLEESDIKTE_MIN = 55;
const VLEESDIKTE_MAX = 80;
const VLEESDIKTE_SEGMENT_COUNT = 5;
const VLEESDIKTE_LABELS = ['60', '65', '70', '75', '80'];
const VLEESDIKTE_COLORS = ['#e07070', '#f0c840', '#F2DCBB', '#a0d48a', '#2d6a2d'];

export function getSegmentColor(index) {
  if (index <= 3) return '#2d6a2d'; // dark green
  if (index <= 4) return '#a0d48a'; // light green
  if (index <= 7) return '#f0c840'; // yellow-orange
  return '#e07070';                  // red
}

function getKwaliteitstoeslagSegmentColor(index) {
  const reversedIndex = SEGMENT_COLORS.length - index;
  return SEGMENT_COLORS[reversedIndex] || SEGMENT_COLORS[0];
}

function getVleesdikteSegmentColor(index) {
  return VLEESDIKTE_COLORS[index - 1] || VLEESDIKTE_COLORS[VLEESDIKTE_COLORS.length - 1];
}

function valueToAngle(v) {
  return START_ANGLE + ((v - MIN) / (MAX - MIN)) * (END_ANGLE - START_ANGLE);
}

function valueToAngleMirrored(v) {
  return END_ANGLE - ((v - MIN) / (MAX - MIN)) * (END_ANGLE - START_ANGLE);
}


/**
 * Build 15 equal gauge segments from the klassement array.
 * klassement[i]["271"] = price class (1–15)
 * klassement[i]["275"] = animal count for that price class
 * Missing price classes get count 0.
 * Segments 1–14 carry a scale label; segment 15 has none.
 */
export function buildSegments(klassement, colorGetter = getSegmentColor, labels = SEGMENT_LABELS) {
  const countByClass = {};
  if (Array.isArray(klassement)) {
    klassement.forEach(item => {
      const priceClass = parseInt(item['271'], 10);
      const count = parseInt(item['275'], 10);
      if (!isNaN(priceClass) && !isNaN(count)) {
        countByClass[priceClass] = count;
      }
    });
  }

  const segWidth = (MAX - MIN) / NUM_SEGMENTS;
  return Array.from({ length: NUM_SEGMENTS }, (_, i) => ({
    priceClass: i + 1,
    min: MIN + i * segWidth,
    max: MIN + (i + 1) * segWidth,
    color: colorGetter(i + 1),
    count: countByClass[i + 1] || 0,
    label: i < labels.length ? labels[i] : null,
  }));
}

// Bands are 55-60, 60-65, 65-70, 70-75 and 75-80. A value below 55 counts in
// the first band and one of 80 or above in the last.
function getVleesdikteSegmentIndex(value) {
  const segmentWidth = (VLEESDIKTE_MAX - VLEESDIKTE_MIN) / VLEESDIKTE_SEGMENT_COUNT;
  const index = Math.floor((value - VLEESDIKTE_MIN) / segmentWidth);
  return Math.min(Math.max(index, 0), VLEESDIKTE_SEGMENT_COUNT - 1);
}

// Keep a needle on the arc when its value falls outside the scale.
function vleesdikteNeedleAngle(value) {
  const clamped = Math.min(Math.max(value, VLEESDIKTE_MIN), VLEESDIKTE_MAX);
  return valueToAngleForRange(clamped, VLEESDIKTE_MIN, VLEESDIKTE_MAX);
}

/**
 * Build the vleesdikte bands from the klassement.
 *
 * klassement[i]['282'] = that price class's average VLD 3.4, which places it
 *                        in a band
 * klassement[i]['274'] = 'Totaal aantal per PK', the pigs in that price class
 *
 * The band's share of the delivery is computed - count over totalPigs (field
 * 234, 'Tot Totaal') times 100 - rather than read from field 275, which the
 * backend has already rounded to a whole number.
 *
 * Rows with no 282 cannot be placed and are skipped.
 */
export function buildVleesdikteSegments(klassement, totalPigs) {
  const totals = Array(VLEESDIKTE_SEGMENT_COUNT).fill(0);

  // No total means no share can be computed. The bands then read 0%, which
  // shows the data is missing rather than hiding it behind a substitute
  // denominator.
  if (Array.isArray(klassement) && totalPigs > 0) {
    klassement.forEach((item) => {
      const vleesdikte = parseLocaleNumber(item['282'], null);
      if (vleesdikte == null) {
        return;
      }
      const count = parseLocaleNumber(item['274'], 0);
      totals[getVleesdikteSegmentIndex(vleesdikte)] += (count / totalPigs) * 100;
    });
  }

  const segmentWidth = (VLEESDIKTE_MAX - VLEESDIKTE_MIN) / VLEESDIKTE_SEGMENT_COUNT;
  return Array.from({ length: VLEESDIKTE_SEGMENT_COUNT }, (_, i) => ({
    min: VLEESDIKTE_MIN + i * segmentWidth,
    max: VLEESDIKTE_MIN + (i + 1) * segmentWidth,
    color: getVleesdikteSegmentColor(i + 1),
    count: totals[i],
    label: VLEESDIKTE_LABELS[i],
  }));
}

function GaugeChart({ segments, zValue, bpgValue, mirrored, labelOffset = 10, showBpgNeedle = true, needleScaleValues = SEGMENT_LABELS, filterId, ariaLabel }) {
  return (
    <svg
      viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
      role="img"
      aria-label={ariaLabel}
    >
      <defs>
        <filter id={filterId} x="-20%" y="-20%" width="140%" height="140%">
          <feDropShadow dx="1" dy="1" stdDeviation="2" floodOpacity="0.3" />
        </filter>
      </defs>

      {segments.map((seg, i) => {
        const a1 = mirrored ? valueToAngleMirrored(seg.max) : valueToAngle(seg.min);
        const a2 = mirrored ? valueToAngleMirrored(seg.min) : valueToAngle(seg.max);
        const midAngle = (a1 + a2) / 2;
        const midR = (OUTER_R + INNER_R) / 2;
        const mid = polarToXY(midAngle, midR);
        const labelPos = seg.label != null
          ? polarToXY(mirrored ? valueToAngleMirrored(seg.max) : valueToAngle(seg.max), OUTER_R + labelOffset)
          : null;
        return (
          <g key={i}>
            <path d={arcPath(a1, a2, OUTER_R, INNER_R)} fill={seg.color} stroke="white" strokeWidth="0.8" />

            {/* Scale label at upper-right corner; last segment has none */}
            {seg.label != null && (
              <text
                x={labelPos.x}
                y={labelPos.y}
                textAnchor="middle"
              fontSize="11"
              fontWeight="bold"
              fill="#000"
              >
                {seg.label}
              </text>
            )}

            {/* Count inside segment */}
            {seg.count > 0 && (
              <text
                x={mid.x}
                y={mid.y + 5}
                textAnchor="middle"
                fontSize="11"
                fontWeight="500"
                fill="#fff"
                pointerEvents="none"
              >
                {seg.count}
              </text>
            )}
          </g>
        );
      })}

      {/* Z needle (red) */}
      {zValue != null && (
        <path
          d={needlePath(needleValueToAngle(zValue, needleScaleValues, NUM_SEGMENTS), INNER_R - 10, 5)}
          fill="#dc2626"
          filter={`url(#${filterId})`}
        />
      )}

      {showBpgNeedle && bpgValue != null && (
        <path
          d={needlePath(needleValueToAngle(bpgValue, needleScaleValues, NUM_SEGMENTS), ((INNER_R - 10) * 3) / 4, 5)}
          fill="#000"
          filter={`url(#${filterId})`}
        />
      )}

      {/* Center pivot */}
      <circle cx={CX} cy={CY} r={10} fill="#888" />
      <circle cx={CX} cy={CY} r={5} fill="#fff" />
    </svg>
  );
}

function VleesdikteGaugeChart({ segments, labelOffset = 22, zValue = null, bpgValue = null, showBpgNeedle = false, filterId = 'vleesdikte-needle-shadow' }) {
  return (
    <svg
      viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
      role="img"
      aria-label="Vleesdikte gauge"
    >
      <defs>
        <filter id={filterId} x="-20%" y="-20%" width="140%" height="140%">
          <feDropShadow dx="1" dy="1" stdDeviation="2" floodOpacity="0.3" />
        </filter>
      </defs>

      <text
        x={polarToXY(valueToAngleForRange(VLEESDIKTE_MIN, VLEESDIKTE_MIN, VLEESDIKTE_MAX), OUTER_R + labelOffset).x}
        y={polarToXY(valueToAngleForRange(VLEESDIKTE_MIN, VLEESDIKTE_MIN, VLEESDIKTE_MAX), OUTER_R + labelOffset).y}
        textAnchor="middle"
        fontSize="11"
        fontWeight="bold"
        fill="#000"
      >
        {VLEESDIKTE_MIN}
      </text>

      {segments.map((seg, i) => {
        const a1 = valueToAngleForRange(seg.min, VLEESDIKTE_MIN, VLEESDIKTE_MAX);
        const a2 = valueToAngleForRange(seg.max, VLEESDIKTE_MIN, VLEESDIKTE_MAX);
        const midAngle = (a1 + a2) / 2;
        const midR = (OUTER_R + INNER_R) / 2;
        const mid = polarToXY(midAngle, midR);
        const labelPos = polarToXY(a2, OUTER_R + labelOffset);

        return (
          <g key={i}>
            <path d={arcPath(a1, a2, OUTER_R, INNER_R)} fill={seg.color} stroke="white" strokeWidth="0.8" />

            <text
              x={labelPos.x}
              y={labelPos.y}
              textAnchor="middle"
              fontSize="11"
              fontWeight="bold"
              fill="#000"
            >
              {seg.label}
            </text>

            {seg.count > 0 && (
              <text
                data-share
                x={mid.x}
                y={mid.y + 5}
                textAnchor="middle"
                fontSize="11"
                fontWeight="500"
                fill="#fff"
                pointerEvents="none"
              >
                {formatPercentage(seg.count)}
              </text>
            )}
          </g>
        );
      })}

      {zValue != null && (
        <path
          d={needlePath(vleesdikteNeedleAngle(zValue), INNER_R - 10, 5)}
          fill="#dc2626"
          filter={`url(#${filterId})`}
        />
      )}

      {showBpgNeedle && bpgValue != null && (
        <path
          d={needlePath(vleesdikteNeedleAngle(bpgValue), ((INNER_R - 10) * 3) / 4, 5)}
          fill="#000"
          filter={`url(#${filterId})`}
        />
      )}

      <circle cx={CX} cy={CY} r={10} fill="#888" />
      <circle cx={CX} cy={CY} r={5} fill="#fff" />
    </svg>
  );
}

function VleesdikteLegend({ totaalValue, zValue, bValue, mValue, pValue, bpgValue, fields }) {
  const source = useFooterSource();
  // Every gauge lists its rows in the same order: Z, M, B, P, Totaal, BPG.
  const rows = [
    { label: 'Z', avg: zValue, color: null },
    { label: 'M', avg: mValue, color: null },
    { label: 'B', avg: bValue, color: null },
    { label: 'P', avg: pValue, color: null },
    { label: 'Totaal', avg: totaalValue, color: '#dc2626' },
    { label: 'BPG', avg: bpgValue, color: '#000' },
  ]
    // A benchmark the webservice does not send has no row at all.
    .filter((r) => r.avg != null)
    // A zero row tells the grower nothing, as on the other gauges - but in
    // development and staging every row stays, so the field wiring can be
    // checked against the payload.
    .filter((r) => source.available || r.avg !== 0);

  return (
    <div style={{ fontSize: 13, color: '#555', lineHeight: '1.8', textAlign: 'center' }}>
      {rows.map((r, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
          {r.color ? (
            <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 2, background: r.color }} />
          ) : (
            <span style={{ display: 'inline-block', width: 10 }} />
          )}
          <span>{`${r.label} (AVG: ${r.avg.toFixed(2)})`}</span>
          <FieldSource source={fields && fields[r.label]} shown={source.shown} />
        </div>
      ))}
      <FooterSourceToggle
        available={source.available}
        shown={source.shown}
        onToggle={source.toggle}
      />
    </div>
  );
}

function Legend({ bValue, zValue, pValue, mValue, totaalValue, bpgValue, needleOnly, fields, decimals = 2 }) {
  const source = useFooterSource();
  // Every gauge lists its rows in the same order: Z, M, B, P, Totaal, BPG.
  const rows = [
    { label: 'Z',      avg: zValue,   color: null      },
    { label: 'M',      avg: mValue,   color: null      },
    { label: 'B',      avg: bValue,   color: null      },
    { label: 'P',      avg: pValue,   color: null      },
    { label: 'Totaal', avg: totaalValue, color: '#dc2626' },
    { label: 'BPG',    avg: bpgValue, color: '#000' },
  ]
    // A benchmark the webservice does not send has no row at all.
    .filter((row) => row.avg != null)
    .filter((row) => !needleOnly || row.label === 'Totaal' || row.label === 'BPG')
    // Empty rows are dropped outside development and staging; see above.
    .filter((row) => source.available || row.avg !== 0);

  return (
    <div style={{ fontSize: 13, color: '#555', lineHeight: '1.8', textAlign: 'center' }}>
      {rows.map((r, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
          {r.color ? (
            <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 2, background: r.color }} />
          ) : (
            <span style={{ display: 'inline-block', width: 10 }} />
          )}
          <span>{`${r.label} (AVG: ${r.avg.toFixed(decimals)})`}</span>
          <FieldSource source={fields && fields[r.label]} shown={source.shown} />
        </div>
      ))}
      <FooterSourceToggle
        available={source.available}
        shown={source.shown}
        onToggle={source.toggle}
      />
    </div>
  );
}

function ChartPanel({ title, segments, zValue, bpgValue, mirrored, labelOffset, bValue, zLegendValue, pValue, mValue, totaalValue, bpgLegendValue, needleOnlyLegend, showBpgNeedle, needleScaleValues, filterId, ariaLabel, source, legendFields, legendDecimals }) {
  return (
    <div className='statistics-chart-panel'>
      <div className='statistics-chart-header'>
        <h3>{title}</h3>
        <SourceDataButton title={title} source={source} />
      </div>
      <GaugeChart segments={segments} zValue={zValue} bpgValue={bpgValue} mirrored={mirrored} labelOffset={labelOffset} showBpgNeedle={showBpgNeedle} needleScaleValues={needleScaleValues} filterId={filterId} ariaLabel={ariaLabel} />
      <Legend
        bValue={bValue}
        zValue={zLegendValue}
        pValue={pValue}
        mValue={mValue}
        totaalValue={totaalValue}
        bpgValue={bpgLegendValue}
        needleOnly={needleOnlyLegend}
        fields={legendFields}
        decimals={legendDecimals}
      />
    </div>
  );
}

function VleesdikteChartPanel({ title, segments, zValue, bpgValue, totaalValue, zLegendValue, bValue, mValue, pValue, source, fields }) {
  return (
    <div className='statistics-chart-panel'>
      <div className='statistics-chart-header'>
        <h3>{title}</h3>
        <SourceDataButton title={title} source={source} />
      </div>
      <VleesdikteGaugeChart segments={segments} zValue={zValue} bpgValue={bpgValue} showBpgNeedle={true} />
      <VleesdikteLegend
        totaalValue={totaalValue}
        zValue={zLegendValue}
        bValue={bValue}
        mValue={mValue}
        pValue={pValue}
        bpgValue={bpgValue}
        fields={fields}
      />
    </div>
  );
}

/*
 * The bare gauges, for pages that only have two averages to show and no
 * klassement: the segments carry their colours and scale labels but no counts,
 * and there is no footer. A needle whose value is missing is not drawn.
 */
export function AfiAverageGauge({ redValue, blackValue }) {
  return (
    <GaugeChart
      segments={buildSegments(null)}
      zValue={redValue}
      bpgValue={blackValue}
      mirrored={false}
      labelOffset={22}
      needleScaleValues={SEGMENT_LABELS}
      filterId='afi-needle-shadow'
      ariaLabel='AFI gauge'
    />
  );
}

export function KwaliteitstoeslagAverageGauge({ redValue, blackValue }) {
  return (
    <GaugeChart
      segments={buildSegments(
        null,
        getKwaliteitstoeslagSegmentColor,
        [...KWALITEITSTOESLAG_LABELS].reverse()
      )}
      zValue={redValue}
      bpgValue={blackValue}
      mirrored={true}
      labelOffset={22}
      needleScaleValues={KWALITEITSTOESLAG_VALUES}
      filterId='kwaliteitstoeslag-needle-shadow'
      ariaLabel='Kwaliteitstoeslag gauge'
    />
  );
}

export function VleesdikteAverageGauge({ redValue, blackValue }) {
  return (
    <VleesdikteGaugeChart
      segments={buildVleesdikteSegments(null, 0)}
      zValue={redValue}
      bpgValue={blackValue}
      showBpgNeedle={true}
    />
  );
}

function StatisticsChart({ data, afiBenchmarkValue, vleesdikteBenchmarkValue, gewichtBenchmarkValue, spekdikteBenchmarkValue, kwaliteitstoeslagBenchmarkValue, gewichtstoeslagBenchmarkValue }) {
  const afiSegments = buildSegments(data ? data.klassement : null);
  const kwaliteitstoeslagSegments = buildSegments(
    data ? data.klassement : null,
    getKwaliteitstoeslagSegmentColor,
    [...KWALITEITSTOESLAG_LABELS].reverse()
  );
  // Field 234 = 'Tot Totaal', the pigs in the whole delivery.
  const totalPigs = parseLocaleNumber(data && data.klassement_totalen && data.klassement_totalen['234'], 0);
  const vleesdikteSegments = buildVleesdikteSegments(data ? data.klassement : null, totalPigs);

  const kt = data && data.klassement_totalen;
  const parseVal = (raw, fallback = 0) => parseLocaleNumber(raw, fallback);

  // Per fieldLabels.js: 195 is 'Z - Gem. AFI/MBI', 225 is 'M - ...' and 515 is
  // 'B - ...'. These four were previously wired one role out of step, so the
  // footer showed each value under the wrong letter. 545 is the fourth member
  // of the family and so the P row - fieldLabels spells it 'Z', duplicating 195.
  const zLegendValue = parseVal(kt && kt['195']);        // Z
  const mValue      = parseVal(kt && kt['225']);         // M
  const bValue      = parseVal(kt && kt['515']);         // B
  const pValue      = parseVal(kt && kt['545']);         // P
  const totaalValue = parseVal(kt && kt['245'], 3.29);  // AFI Totaal
  const afiZValue   = totaalValue;                       // AFI red needle = Totaal
  const kwaliteitstoeslagTotaalValue = parseVal(kt && kt['332']); // Kwaliteitstoeslag Totaal / red needle
  const kwaliteitstoeslagZValue = kwaliteitstoeslagTotaalValue;
  // The gauge measures VLD 3.4, not Vle P2: 242 / 192 / 222 / 512 / 542 rather
  // than 239 / 189 / 219 / 509 / 539. Same measuring point convention the
  // spekdikte gauge uses with SP 3.4.
  const vleesdikteZValue = parseVal(kt && kt['242']);   // Vleesdikte red needle
  const vleesdikteLegendTotaalValue = parseVal(kt && kt['242']);
  const vleesdikteLegendZValue = parseVal(kt && kt['192']);
  const vleesdikteLegendBValue = parseVal(kt && kt['512']);
  const vleesdikteLegendMValue = parseVal(kt && kt['222']);
  const vleesdikteLegendPValue = parseVal(kt && kt['542']);
  const bpgValue    = afiBenchmarkValue != null
    ? afiBenchmarkValue
    : parseVal(kt && kt['245'], 3.29);                  // Blue needle = BPG AVG benchmark
  const bpgLegendValue = afiBenchmarkValue != null
    ? afiBenchmarkValue
    : bpgValue;

  // The payload slice each gauge reads, for the source-data dialog.
  const klassementRows = data ? data.klassement : null;
  const afiSource = {
    klassement: klassementRows,
    klassement_totalen: pickFields(kt, ['245', '195', '225', '515', '545']),
    benchmark: { source: 'drgmbmk', field: 'DEGEMMB', deltmce: 'ALL', value: afiBenchmarkValue },
  };
  const kwaliteitstoeslagSource = {
    klassement: klassementRows,
    klassement_totalen: pickFields(kt, ['332']),
  };
  const vleesdikteSource = {
    klassement: klassementRows,
    klassement_totalen: pickFields(kt, ['242', '192', '512', '222', '542', '234']),
    benchmark: { source: 'drgmbmk', field: 'DEGEMVD', deltmce: 'ALL', value: vleesdikteBenchmarkValue },
  };

  return (
    <div className='statistics-charts'>
      <ChartPanel
        title='AFI'
        segments={afiSegments}
        zValue={afiZValue}
        bpgValue={bpgValue}
        mirrored={false}
        labelOffset={22}
        bValue={bValue}
        zLegendValue={zLegendValue}
        pValue={pValue}
        mValue={mValue}
        totaalValue={totaalValue}
        bpgLegendValue={bpgLegendValue}
        needleOnlyLegend={false}
        showBpgNeedle={true}
        needleScaleValues={SEGMENT_LABELS}
        filterId='afi-needle-shadow'
        ariaLabel='AFI gauge'
        legendFields={{
          Totaal: '245', Z: '195', B: '515', M: '225', P: '545',
          BPG: 'BPG benchmark (drgmbmk DEGEMMB, week -1, ALL)',
        }}
        source={afiSource}
      />
      <ChartPanel
        title='Kwaliteitstoeslag'
        segments={kwaliteitstoeslagSegments}
        zValue={kwaliteitstoeslagZValue}
        bpgValue={kwaliteitstoeslagBenchmarkValue}
        mirrored={true}
        labelOffset={22}
        bValue={bValue}
        zLegendValue={zLegendValue}
        pValue={pValue}
        mValue={mValue}
        totaalValue={kwaliteitstoeslagTotaalValue}
        bpgLegendValue={kwaliteitstoeslagBenchmarkValue}
        needleOnlyLegend={true}
        showBpgNeedle={kwaliteitstoeslagBenchmarkValue != null}
        needleScaleValues={KWALITEITSTOESLAG_VALUES}
        legendDecimals={KWALITEITSTOESLAG_DECIMALS}
        filterId='kwaliteitstoeslag-needle-shadow'
        ariaLabel='Kwaliteitstoeslag gauge'
        legendFields={{
          Totaal: '332',
          BPG: 'BPG benchmark (drgmbmk DEGEMKT, week -1, ALL)',
        }}
        source={kwaliteitstoeslagSource}
      />
      <VleesdikteChartPanel
        title='Vleesdikte'
        segments={vleesdikteSegments}
        zValue={vleesdikteZValue}
        bpgValue={vleesdikteBenchmarkValue}
        totaalValue={vleesdikteLegendTotaalValue}
        zLegendValue={vleesdikteLegendZValue}
        bValue={vleesdikteLegendBValue}
        mValue={vleesdikteLegendMValue}
        pValue={vleesdikteLegendPValue}
        fields={{
          Totaal: '242', Z: '192', B: '512', M: '222', P: '542',
          BPG: 'BPG benchmark (drgmbmk DEGEMVD, week -1, ALL)',
        }}
        source={vleesdikteSource}
      />
      <GewichtChartPanel
        data={data}
        bpgValue={gewichtBenchmarkValue}
      />
      <GewichtstoeslagChartPanel
        data={data}
        bpgValue={gewichtstoeslagBenchmarkValue}
      />
      <SpekdikteChartPanel
        data={data}
        bpgValue={spekdikteBenchmarkValue}
      />
    </div>
  );
}

StatisticsChart.propTypes = {
  data: PropTypes.object,
  afiBenchmarkValue: PropTypes.number,
  vleesdikteBenchmarkValue: PropTypes.number,
  gewichtBenchmarkValue: PropTypes.number,
  spekdikteBenchmarkValue: PropTypes.number,
  kwaliteitstoeslagBenchmarkValue: PropTypes.number,
  gewichtstoeslagBenchmarkValue: PropTypes.number,
};

StatisticsChart.defaultProps = {
  data: null,
  afiBenchmarkValue: null,
  vleesdikteBenchmarkValue: null,
  gewichtBenchmarkValue: null,
  spekdikteBenchmarkValue: null,
  kwaliteitstoeslagBenchmarkValue: null,
  gewichtstoeslagBenchmarkValue: null,
};

export default StatisticsChart;
